import { useNavigate, useParams } from "react-router-dom";
import { useExam, useStartExam } from "@/hooks/useExam";
import { useExamStore } from "@/stores/examStore";
import { useAuthStore } from "@/stores/authStore";

export default function ExamDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: exam, isLoading, error } = useExam(id || "");
  const startExamMutation = useStartExam();
  const { startExam } = useExamStore();
  const { isAuthenticated } = useAuthStore();

  const handleStartExam = async () => {
    if (!isAuthenticated) {
      navigate("/login", { state: { from: { pathname: `/exams/${id}` } } });
      return;
    }
    if (!exam || !id) return;
    try {
      const attempt = await startExamMutation.mutateAsync(id);
      startExam(exam, attempt.attempt_id);
      navigate(`/exams/${id}/take`);
    } catch {
      // Error handled by mutation
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <svg className="animate-spin h-8 w-8 text-primary-600" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      </div>
    );
  }

  if (error || !exam) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-20 text-center">
        <p className="text-danger-600">Failed to load exam details.</p>
      </div>
    );
  }

  return (
    <div className="page-enter mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-8">
      <div className="rounded-xl border border-gray-200 bg-white p-8 shadow-sm">
        <h1 className="text-2xl font-bold text-gray-900">{exam.title}</h1>
        {exam.description && (
          <p className="mt-2 text-gray-500">{exam.description}</p>
        )}

        <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-6">
          <div className="rounded-lg bg-primary-50 p-4 text-center">
            <div className="text-3xl font-bold text-primary-700">{exam.total_questions}</div>
            <div className="text-sm text-primary-600 mt-1">Questions</div>
          </div>
          <div className="rounded-lg bg-primary-50 p-4 text-center">
            <div className="text-3xl font-bold text-primary-700">{exam.time_limit_minutes}</div>
            <div className="text-sm text-primary-600 mt-1">Minutes</div>
          </div>
          <div className="rounded-lg bg-success-50 p-4 text-center">
            <div className="text-3xl font-bold text-success-700">{exam.pass_mark}/{exam.total_questions}</div>
            <div className="text-sm text-success-600 mt-1">Pass Mark</div>
          </div>
        </div>

        <div className="mt-8 rounded-lg bg-gray-50 p-5">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Before you start:</h3>
          <ul className="space-y-2 text-sm text-gray-600">
            <li className="flex items-start space-x-2">
              <svg className="h-5 w-5 text-primary-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span>You will have {exam.time_limit_minutes} minutes to complete all {exam.total_questions} questions</span>
            </li>
            <li className="flex items-start space-x-2">
              <svg className="h-5 w-5 text-primary-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span>Each question has 4 possible answers - select the one you believe is correct</span>
            </li>
            <li className="flex items-start space-x-2">
              <svg className="h-5 w-5 text-primary-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span>You can navigate between questions and change your answers</span>
            </li>
            <li className="flex items-start space-x-2">
              <svg className="h-5 w-5 text-primary-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span>You need at least {exam.pass_mark} correct answers to pass ({Math.round((exam.pass_mark / exam.total_questions) * 100)}%)</span>
            </li>
            <li className="flex items-start space-x-2">
              <svg className="h-5 w-5 text-primary-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span>Some questions may include images or video clips</span>
            </li>
          </ul>
        </div>

        <div className="mt-8 flex justify-center">
          <button
            onClick={handleStartExam}
            disabled={startExamMutation.isPending}
            className="rounded-lg bg-primary-600 px-10 py-3 text-base font-semibold text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {startExamMutation.isPending ? (
              <span className="flex items-center space-x-2">
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                <span>Starting...</span>
              </span>
            ) : (
              "Start Exam"
            )}
          </button>
        </div>

        {startExamMutation.error && (
          <p className="mt-4 text-center text-sm text-danger-600">
            Failed to start exam. Please try again.
          </p>
        )}
      </div>
    </div>
  );
}
